import {
  CompositeFilterDescriptor,
  FilterDescriptor
} from '@progress/kendo-data-query';
import { Dispatch, SetStateAction } from 'react';
import { CpsFilterRequest, CpsSearchQueryRequest, PERMISSIONS_FIELD } from '@cs-cms/types';

export const removeGlobalFilterFromQuery = (
  queryRequest: CpsSearchQueryRequest,
  fieldToRemove: string,
  valueToRemove?: string
): CpsSearchQueryRequest => {
  const updatedFilters = (queryRequest.filters ?? [])
    .map(filter => {
      if (filter.fieldName !== fieldToRemove || !filter.isGlobal) {
        return filter;
      }

      if (!valueToRemove) {
        return null;
      }

      const filterValues = filter.values;
      if (!filterValues || !Array.isArray(filterValues)) {
        return null;
      }

      const valuesArray = filterValues as Array<string | number | boolean>;
      const updatedValues = valuesArray.filter(v => v?.toString() !== valueToRemove);

      if (updatedValues.length === 0) {
        return null;
      }

      return {
        ...filter,
        values: updatedValues
      };
    })
    .filter((filter): filter is CpsFilterRequest => filter !== null);

  return {
    ...queryRequest,
    filters: updatedFilters
  };
};

export const removeActionsFilterFromQuery = (
  queryRequest: CpsSearchQueryRequest,
  valueToRemove?: string
): CpsSearchQueryRequest => {
  if (!valueToRemove) {
    return {
      ...queryRequest,
      actionsFilter: undefined
    };
  }

  const updatedActionsFilter = queryRequest.actionsFilter?.filter(action => action.toString() !== valueToRemove);

  return {
    ...queryRequest,
    actionsFilter: updatedActionsFilter && updatedActionsFilter.length > 0 ? updatedActionsFilter : undefined
  };
};

export const removeGridColumnFilterFromComposite = (
  compositeFilterDescriptor: CompositeFilterDescriptor,
  fieldToRemove: string,
  valueToRemove?: string
): CompositeFilterDescriptor => {
  const newFilters = compositeFilterDescriptor.filters
    .map(descriptor => {
      const composite = descriptor as CompositeFilterDescriptor;
      if (composite.filters.length === 0) {
        return composite;
      }

      const firstFilter = composite.filters[0] as FilterDescriptor;
      if (firstFilter.field !== fieldToRemove) {
        return composite;
      }

      if (!valueToRemove) {
        return { ...composite, filters: [] };
      }

      const updatedFilters = composite.filters.filter(
        f => (f as FilterDescriptor).value?.toString() !== valueToRemove
      );

      return { ...composite, filters: updatedFilters };
    })
    .filter(d => d.filters.length > 0);

  return {
    logic: compositeFilterDescriptor.logic,
    filters: newFilters
  };
};

export const createRemoveFilterHandler = (params: {
  queryRequest: CpsSearchQueryRequest | undefined;
  setQueryRequest: Dispatch<SetStateAction<CpsSearchQueryRequest | undefined>> | undefined;
  compositeFilterDescriptor: CompositeFilterDescriptor | undefined;
  setCompositeFilterDescriptor: ((descriptor: CompositeFilterDescriptor) => void) | undefined;
}): ((fieldToRemove: string, valueToRemove?: string) => void) => {
  const { queryRequest, setQueryRequest, compositeFilterDescriptor, setCompositeFilterDescriptor } = params;

  return (fieldToRemove: string, valueToRemove?: string): void => {
    const isGlobalFilter = queryRequest?.filters?.some(f => f.fieldName === fieldToRemove && f.isGlobal);

    if (isGlobalFilter && queryRequest && setQueryRequest) {
      setQueryRequest(removeGlobalFilterFromQuery(queryRequest, fieldToRemove, valueToRemove));
      return;
    }

    if (fieldToRemove === PERMISSIONS_FIELD && queryRequest && setQueryRequest) {
      setQueryRequest(removeActionsFilterFromQuery(queryRequest, valueToRemove));
      return;
    }

    if (compositeFilterDescriptor && setCompositeFilterDescriptor) {
      setCompositeFilterDescriptor(
        removeGridColumnFilterFromComposite(compositeFilterDescriptor, fieldToRemove, valueToRemove)
      );
    }
  };
};
